import { useEffect, useMemo, useState } from "react";
import { Loader2, Sparkles } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";
import { enrollCompany, listCampaigns } from "@/api/outreach";
import { useCurrentProject } from "@/hooks/useCurrentProject";
import type {
  CampaignRead,
  CompanyDetail as CompanyDetailType,
  EmbeddedContact,
  OutreachStatus,
} from "@/api/types";

interface EnrollDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  projectSlug: string;
  company: CompanyDetailType;
  onEnrolled: (next: OutreachStatus) => void;
}

export function EnrollDialog({
  open,
  onOpenChange,
  projectSlug,
  company,
  onEnrolled,
}: EnrollDialogProps) {
  const { project } = useCurrentProject();
  const defaultCampaignId =
    typeof project?.config?.lemlist_campaign_id === "string"
      ? (project.config.lemlist_campaign_id as string)
      : null;

  const eligibleContacts = useMemo<EmbeddedContact[]>(
    () => company.contacts.filter((c) => !!c.email),
    [company.contacts],
  );
  const firstDmWithEmail =
    eligibleContacts.find((c) => c.is_decision_maker) ?? eligibleContacts[0];

  const [campaigns, setCampaigns] = useState<CampaignRead[]>([]);
  const [campaignsLoading, setCampaignsLoading] = useState(false);
  const [campaignId, setCampaignId] = useState<string>("");
  const [contactId, setContactId] = useState<string>("");
  const [icebreaker, setIcebreaker] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    setIcebreaker("");
    setContactId(firstDmWithEmail ? String(firstDmWithEmail.id) : "");
    setCampaignsLoading(true);
    listCampaigns(projectSlug)
      .then((items) => {
        setCampaigns(items);
        const active = items.find((c) => c.status === "active") ?? items[0];
        const preferred =
          (defaultCampaignId &&
            items.find((c) => c.id === defaultCampaignId)?.id) ||
          active?.id ||
          "";
        setCampaignId(preferred);
      })
      .catch(() => {
        setCampaigns([]);
        setCampaignId("");
      })
      .finally(() => setCampaignsLoading(false));
  }, [open, projectSlug, defaultCampaignId, firstDmWithEmail]);

  const noEmail = eligibleContacts.length === 0;
  const canSubmit = !!campaignId && !!contactId && !submitting && !noEmail;

  async function handleSubmit() {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      const next = await enrollCompany(projectSlug, company.id, {
        campaign_id: campaignId,
        contact_id: Number(contactId),
        custom_icebreaker: icebreaker.trim() ? icebreaker.trim() : null,
      });
      onEnrolled(next);
    } catch (err) {
      toast.error(extractError(err));
    } finally {
      setSubmitting(false);
    }
  }

  // Defensive read — if the backend stamps the previous campaign name on
  // metadata after a hard-delete, surface a re-enrollment hint. Falls back
  // silently when the field isn't present.
  const previousCampaignName =
    typeof company.metadata?.lemlist_previous_campaign_name === "string"
      ? (company.metadata.lemlist_previous_campaign_name as string)
      : null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[520px]">
        <DialogHeader>
          <DialogTitle>Start email sequence for {company.name}</DialogTitle>
        </DialogHeader>

        <div className="space-y-5 py-2">
          {previousCampaignName && (
            <div className="rounded-lg border border-blue-200 bg-blue-50 px-3 py-2 text-xs text-blue-700">
              This contact was previously in campaign{" "}
              <span className="font-medium">{previousCampaignName}</span>.
              They can be re-enrolled.
            </div>
          )}

          <Field label="Campaign">
            <Select
              value={campaignId}
              onValueChange={setCampaignId}
              disabled={campaignsLoading || campaigns.length === 0}
            >
              <SelectTrigger className="h-10 rounded-lg border-slate-200">
                <SelectValue
                  placeholder={
                    campaignsLoading ? "Loading..." : "Select a campaign"
                  }
                />
              </SelectTrigger>
              <SelectContent>
                {campaigns.map((c) => (
                  <SelectItem key={c.id} value={c.id}>
                    <span className="flex items-center gap-2">
                      <span>{c.name}</span>
                      <CampaignStatusBadge status={c.status} />
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {!campaignsLoading && campaigns.length === 0 && (
              <p className="text-xs text-rose-600">
                No campaigns available. Configure Lemlist in settings.
              </p>
            )}
          </Field>

          <Field label="Contact">
            {noEmail ? (
              <p className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-700">
                No contact with email address. Add an email first.
              </p>
            ) : (
              <Select value={contactId} onValueChange={setContactId}>
                <SelectTrigger className="h-10 rounded-lg border-slate-200">
                  <SelectValue placeholder="Select a contact" />
                </SelectTrigger>
                <SelectContent>
                  {eligibleContacts.map((c) => (
                    <SelectItem key={c.id} value={String(c.id)}>
                      <span className="flex items-center gap-2">
                        <span className="font-medium">{c.full_name}</span>
                        <span className="text-xs text-slate-400">
                          {c.email}
                        </span>
                        {c.is_decision_maker && (
                          <span className="rounded-full bg-indigo-50 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-indigo-700">
                            DM
                          </span>
                        )}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </Field>

          <Field label="Icebreaker (optional)">
            <Textarea
              value={icebreaker}
              onChange={(e) => setIcebreaker(e.target.value)}
              rows={4}
              placeholder="Custom opening message (optional). Leave empty to auto-generate with AI."
              className="rounded-lg border-slate-200 text-sm"
              disabled={submitting}
            />
            <button
              type="button"
              onClick={() => setIcebreaker("")}
              className="inline-flex items-center gap-1 text-xs text-indigo-600 hover:text-indigo-500"
            >
              <Sparkles className="h-3 w-3" />
              Generate with AI
            </button>
          </Field>
        </div>

        <DialogFooter>
          <Button
            type="button"
            variant="ghost"
            onClick={() => onOpenChange(false)}
            disabled={submitting}
          >
            Cancel
          </Button>
          <Button
            type="button"
            variant="primary"
            disabled={!canSubmit}
            onClick={handleSubmit}
          >
            {submitting && <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />}
            {submitting ? "Enrolling..." : "Start sequence"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Field({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-1.5">
      <label className="text-[11px] font-medium uppercase tracking-wider text-slate-500">
        {label}
      </label>
      {children}
    </div>
  );
}

function CampaignStatusBadge({ status }: { status: string }) {
  const isActive = status === "active";
  return (
    <span
      className={cn(
        "rounded-full border px-1.5 py-0.5 text-[10px] font-medium capitalize",
        isActive
          ? "border-emerald-200 bg-emerald-50 text-emerald-700"
          : "border-slate-200 bg-slate-100 text-slate-600",
      )}
    >
      {status}
    </span>
  );
}

function extractError(err: unknown): string {
  if (axios.isAxiosError(err)) {
    const detail = (err.response?.data as { detail?: unknown } | undefined)
      ?.detail;
    if (typeof detail === "string") return detail;
  }
  return "Couldn't start the sequence.";
}
